/**
 * Which messages in a device's conversation are new to the reader.
 *
 * Pure, because the rules are small but easy to get subtly wrong in a way
 * nobody notices: counting your own messages as news, or marking a message
 * read that was never on screen.
 */

export interface ReadableMessage {
  created_at: string;
  mine: boolean;
}

/** Messages from other people since the reader last looked. */
export function unreadCount(messages: readonly ReadableMessage[], lastReadAt: string | null): number {
  const since = lastReadAt === null ? -Infinity : Date.parse(lastReadAt);
  return messages.filter((m) => !m.mine && Date.parse(m.created_at) > since).length;
}

/**
 * The point to mark read up to: the newest message actually rendered.
 *
 * Not "now". A message that lands between loading the conversation and
 * marking it read would otherwise be recorded as seen without ever having
 * been on screen.
 */
export function readThrough(messages: readonly ReadableMessage[]): string | null {
  let newest: string | null = null;
  for (const m of messages) {
    if (newest === null || Date.parse(m.created_at) > Date.parse(newest)) newest = m.created_at;
  }
  return newest;
}

/** Whether marking read would move anything, so an idle poll sends nothing. */
export function needsMarking(messages: readonly ReadableMessage[], lastReadAt: string | null): boolean {
  const through = readThrough(messages);
  if (through === null) return false;
  return lastReadAt === null || Date.parse(through) > Date.parse(lastReadAt);
}

/**
 * What a key press in the message box does.
 *
 * Enter sends. Shift+Enter and Ctrl+Enter both make a line: Shift because
 * that is what most chat apps do, Ctrl because others do and people's hands
 * do not check which app they are in. A browser inserts nothing for
 * Ctrl+Enter by itself, so the caller has to put the line break in.
 */
export function enterAction(key: { key: string; shiftKey: boolean; ctrlKey: boolean; isComposing?: boolean }): "send" | "newline" | "default" {
  // Enter while an IME is composing (Japanese, Chinese input) confirms the
  // composition; sending then would post half a word.
  if (key.key !== "Enter" || key.isComposing) return "default";
  if (key.ctrlKey) return "newline";
  if (key.shiftKey) return "default";
  return "send";
}
